'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'

/**
 * VersionChecker - Silently reloads when client code is stale
 *
 * On navigation, compares client build ID against server.
 * If different, does a hard reload to get fresh code.
 * No user prompt - just seamless refresh.
 */
const VersionChecker = () => {
    const pathname = usePathname()
    const initialBuildId = useRef(null)
    const isFirstRender = useRef(true)

    // Capture initial build ID on mount
    useEffect(() => {
        if (typeof window !== 'undefined' && window.__NEXT_DATA__?.buildId) {
            initialBuildId.current = window.__NEXT_DATA__.buildId
        }
    }, [])

    // Check version on pathname change
    useEffect(() => {
        // Skip first render (initial page load)
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }

        // Skip in development
        if (initialBuildId.current === 'development' || !initialBuildId.current) {
            return
        }

        const checkVersion = async () => {
            try {
                const res = await fetch('/api/version', { cache: 'no-store' })
                if (!res.ok) return

                const { buildId } = await res.json()

                if (buildId && buildId !== 'development' && buildId !== initialBuildId.current) {
                    // Stale code - do hard reload
                    window.location.reload()
                }
            } catch {
                // Network error - ignore, don't block navigation
            }
        }

        checkVersion()
    }, [pathname])

    return null
}

export default VersionChecker
